import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { Loader } from 'Src/components/Loader/Loader';
import { AppUser } from 'Src/views/App/App';
import { History, Location } from 'history';
import { updateSchoolSettings } from 'Src/helpers/service/admin/settings';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { uploadImage } from 'Src/helpers/service/image';
import {
  TWITTER_STATUS_OPTIONS,
  getSelectOptionsForAgesGroupNaming,
  getSelectOptionsForPublicSiteAccess,
  getSelectOptionsForStudentNameView
} from 'Src/helpers/select/select';
import { getPostcode, searchFunctionSchoolPostcodes } from 'Src/helpers/autocomplete/autocomplete';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { PhoneCodesList } from 'Src/views/GenericView/Profile/ProfileSummary/PhoneCodesList';
import { PHONE_CODE_WITH_FLAG } from 'Src/consts/common';
import { getPhoneAndCode } from 'Src/helpers/phone/phone';
import { SelectField } from 'Src/components/SelectField/SelectField';
import { SCHOOL_STATUS, DEFAULT_PUBLIC_BIGSCREEN_SITE, DEFAULT_PUBLIC_SITE, KIND } from 'Src/consts/school';
import { checkDomainAvailabilty } from 'Src/helpers/service/public/public';
import { PHONE_CODE } from 'Src/consts/common';
import { Switch } from 'Src/components/Switch/Switch';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import { PHOTO_ACCESS_PRESET_OPTIONS } from 'Src/consts/school';
import { getSchoolPublicSiteLink } from 'Src/helpers/link/link';
import ReactCrop, { PixelCrop, PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './EditSchoolSettings.scss';
import { SchoolSettingsTabs } from '../SchoolSettingsTabs';

const parseSchoolDefaultTime = (value: string): Date | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value || '');

  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  const result = new Date();
  result.setHours(hours, minutes, 0, 0);

  return result;
};

const formatSchoolDefaultTime = (value: Date | null): string => {
  if (!value) {
    return '';
  }

  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  onSchoolUpdate: () => any;
}

interface State {
  isLoading: boolean;
  isPhoneCodeOptionsVisible: boolean;
  isLoadingPhoto: boolean;
  imagePreviewUrl: string;
  isLogoCropOpen: boolean;
  logoSourceUrl: string;
  logoSourceName: string;
  logoSourceType: string;
  logoCrop: PercentCrop;
  completedLogoCrop: PixelCrop | null;
  logoCropError: string;
}

export class EditSchoolSettings extends Component<Props, State> {
  _isMounted = false;
  logoCropImage: HTMLImageElement | null = null;

  constructor(props) {
    super(props);

    this.state = {
      isPhoneCodeOptionsVisible: false,
      isLoading: false,
      isLoadingPhoto: false,
      imagePreviewUrl: '',
      isLogoCropOpen: false,
      logoSourceUrl: '',
      logoSourceName: '',
      logoSourceType: '',
      logoCrop: { unit: '%', x: 5, y: 5, width: 90, height: 90, aspect: 1 },
      completedLogoCrop: null,
      logoCropError: ''
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.state.logoSourceUrl) {
      URL.revokeObjectURL(this.state.logoSourceUrl);
    }
  }

  updateSchool = () => {
    const { onSchoolUpdate } = this.props;

    this.setState({
      isLoading: true
    });

    onSchoolUpdate().then(() => {
      if (this._isMounted) {
        this.setState({
          isLoading: false
        });
      }
    });
  };

  onChangeLogoClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const image = input.files && input.files[0];
    input.value = '';

    if (!image) {
      return;
    }

    const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (acceptedTypes.indexOf(image.type) === -1) {
      this.setState({
        logoCropError: 'Choose a JPG, PNG, WebP or SVG image.'
      });
      return;
    }

    if (this.state.logoSourceUrl) {
      URL.revokeObjectURL(this.state.logoSourceUrl);
    }

    this.logoCropImage = null;
    this.setState({
      isLogoCropOpen: true,
      logoSourceUrl: URL.createObjectURL(image),
      logoSourceName: image.name,
      logoSourceType: image.type,
      logoCrop: { unit: '%', x: 5, y: 5, width: 90, height: 90, aspect: 1 },
      completedLogoCrop: null,
      logoCropError: ''
    });
  };

  closeLogoCrop = () => {
    const { logoSourceUrl } = this.state;
    if (logoSourceUrl) {
      URL.revokeObjectURL(logoSourceUrl);
    }

    this.logoCropImage = null;
    this.setState({
      isLogoCropOpen: false,
      logoSourceUrl: '',
      logoSourceName: '',
      logoSourceType: '',
      completedLogoCrop: null,
      logoCropError: ''
    });
  };

  onLogoCropImageLoad = (image: HTMLImageElement) => {
    this.logoCropImage = image;

    if (image.naturalWidth < 200 || image.naturalHeight < 200) {
      this.setState({
        completedLogoCrop: null,
        logoCropError: `This image is ${image.naturalWidth} x ${image.naturalHeight}px. Choose one that is at least 200 x 200px.`
      });
      return false;
    }

    const imageAspect = image.width / image.height;
    const width = imageAspect > 1 ? 90 / imageAspect : 90;
    const height = imageAspect > 1 ? 90 : 90 * imageAspect;
    const crop: PercentCrop = {
      unit: '%',
      x: (100 - width) / 2,
      y: (100 - height) / 2,
      width,
      height,
      aspect: 1
    };

    this.setState({
      logoCrop: crop,
      completedLogoCrop: {
        unit: 'px',
        x: (crop.x / 100) * image.width,
        y: (crop.y / 100) * image.height,
        width: (crop.width / 100) * image.width,
        height: (crop.height / 100) * image.height,
        aspect: 1
      },
      logoCropError: ''
    });
    return false;
  };

  applyLogoCrop = setFieldValue => {
    const { completedLogoCrop, logoSourceName, logoSourceType, logoSourceUrl } = this.state;
    const image = this.logoCropImage;

    if (!image || !completedLogoCrop || !completedLogoCrop.width || !completedLogoCrop.height) {
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const sourceWidth = completedLogoCrop.width * scaleX;
    const sourceHeight = completedLogoCrop.height * scaleY;

    if (sourceWidth < 200 || sourceHeight < 200) {
      this.setState({ logoCropError: 'Make the selected square at least 200 x 200px.' });
      return;
    }

    const outputSize = Math.max(200, Math.min(1024, Math.round(Math.min(sourceWidth, sourceHeight))));
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const context = canvas.getContext('2d');

    if (!context) {
      this.setState({ logoCropError: 'The image could not be prepared. Please try another file.' });
      return;
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      image,
      completedLogoCrop.x * scaleX,
      completedLogoCrop.y * scaleY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputSize,
      outputSize
    );

    const outputType =
      logoSourceType === 'image/png' || logoSourceType === 'image/svg+xml'
        ? 'image/png'
        : logoSourceType === 'image/webp'
        ? 'image/webp'
        : 'image/jpeg';
    const extension = outputType === 'image/png' ? 'png' : outputType === 'image/webp' ? 'webp' : 'jpg';
    const baseName = logoSourceName.replace(/\.[^/.]+$/, '') || 'school-logo';

    canvas.toBlob(
      blob => {
        if (!blob) {
          this.setState({ logoCropError: 'The image could not be prepared. Please try another file.' });
          return;
        }

        const croppedImage = new File([blob], `${baseName}-square.${extension}`, {
          type: outputType,
          lastModified: Date.now()
        });

        if (logoSourceUrl) {
          URL.revokeObjectURL(logoSourceUrl);
        }
        this.logoCropImage = null;
        this.setState({
          isLogoCropOpen: false,
          logoSourceUrl: '',
          logoSourceName: '',
          logoSourceType: '',
          completedLogoCrop: null,
          logoCropError: '',
          isLoadingPhoto: true
        });

        uploadImage(this.props.user, croppedImage)
          .then(response => {
            if (this._isMounted) {
              const picUrl = `${window.apiImg}/images/${response.key}`;
              setFieldValue('pic', picUrl);
              this.setState({
                imagePreviewUrl: picUrl,
                isLoadingPhoto: false,
                logoCropError: ''
              });
            }
          })
          .catch(() => {
            if (this._isMounted) {
              this.setState({
                isLoadingPhoto: false,
                logoCropError: 'The logo could not be uploaded. Please try again.'
              });
            }
          });
      },
      outputType,
      0.92
    );
  };

  renderLogoCropModal = setFieldValue => {
    const { isLogoCropOpen, logoSourceUrl, logoCrop, completedLogoCrop, logoCropError } = this.state;

    if (!isLogoCropOpen) {
      return null;
    }

    return (
      <div className="schoolSettingsLogoCropOverlay" onMouseDown={this.closeLogoCrop}>
        <div
          className="schoolSettingsLogoCropModal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schoolSettingsLogoCropTitle"
          onMouseDown={event => event.stopPropagation()}
        >
          <div className="schoolSettingsLogoCropModal__header">
            <div>
              <h2 id="schoolSettingsLogoCropTitle">Crop school logo</h2>
              <p>Choose the square area used across Squad In Touch and your public school website.</p>
            </div>
            <button type="button" onClick={this.closeLogoCrop} aria-label="Close logo crop">
              ×
            </button>
          </div>
          <div className="schoolSettingsLogoCropModal__body">
            <div className="schoolSettingsLogoCropModal__badge">Square 1:1 · minimum 200 x 200px</div>
            <div className="schoolSettingsLogoCropModal__canvas">
              <ReactCrop
                src={logoSourceUrl}
                crop={logoCrop}
                minWidth={40}
                minHeight={40}
                keepSelection
                onImageLoaded={this.onLogoCropImageLoad}
                onChange={crop => this.setState({ logoCrop: crop as PercentCrop, logoCropError: '' })}
                onComplete={pixelCrop => this.setState({ completedLogoCrop: pixelCrop as PixelCrop })}
              />
            </div>
            {logoCropError && <div className="schoolSettingsLogoCropModal__error">{logoCropError}</div>}
          </div>
          <div className="schoolSettingsLogoCropModal__footer">
            <button
              type="button"
              className="schoolSettingsButton schoolSettingsButton--secondary"
              onClick={this.closeLogoCrop}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schoolSettingsButton schoolSettingsButton--primary"
              disabled={!completedLogoCrop}
              onClick={() => this.applyLogoCrop(setFieldValue)}
            >
              Use this crop
            </button>
          </div>
        </div>
      </div>
    );
  };

  onPhoneCodeClick = () => {
    const { isPhoneCodeOptionsVisible } = this.state;
    this.setState({
      isPhoneCodeOptionsVisible: !isPhoneCodeOptionsVisible
    });
  };

  getPhoneCodeWithFlag = (phoneCode: string) => {
    for (const key in PHONE_CODE_WITH_FLAG) {
      if (PHONE_CODE_WITH_FLAG.hasOwnProperty(key)) {
        const country = PHONE_CODE_WITH_FLAG[key];
        const { code, flag, alt } = country;

        if (code === phoneCode) {
          return (
            <>
              <img src={`/dist/images/flags/${flag}`} alt={alt} className="schoolSettingsPhone__flag" />
              <span className="schoolSettingsPhone__code">{code}</span>
              <span className="schoolSettingsPhone__chevron" aria-hidden="true" />
            </>
          );
        }
      }
    }
  };

  getSchoolSchema = () => {
    const { user } = this.props;

    const domainErrorText = 'This domain name is already taken. Please select another domain name for your school.';

    const schema = Yup.object().shape({
      name: Yup.string().required('Required'),
      email: Yup.string().email('Must be valid email'),
      sportsDepartmentEmail: Yup.string().email('Must be valid email'),
      postcode: Yup.object()
        .shape({
          postcode: Yup.string().required('Required')
        })
        .required('Required'),
      domain: Yup.string().test('domain', domainErrorText, value => {
        const nextDomain = String(value || '')
          .trim()
          .toLowerCase();

        if (!nextDomain) {
          return true;
        }

        const prevDomain = String(propz.get(user, ['activeSchool', 'domain'], ''))
          .trim()
          .toLowerCase();
        const isDomainChanged = prevDomain !== nextDomain;

        return isDomainChanged
          ? checkDomainAvailabilty(user, nextDomain).then(response => {
              const { isAvailable } = response;

              return isAvailable;
            })
          : true;
      }),
      isEventUpdateNotificationCustomMessageEnabled: Yup.boolean().required('Required'),
      photoAccessPreset: Yup.mixed()
        .oneOf(['PRIVATE', 'ALL_PARTICIPANT_PARENTS'])
        .required('Required')
    });

    return schema;
  };

  onSubmit = values => {
    const { user } = this.props;

    const domain = String(values.domain || '')
      .trim()
      .toLowerCase();
    const data = {
      ...values,
      domain: domain || null,
      ownerId: values.ownerId,
      name: values.name,
      postcode: {
        point: values.postcode.point
      },
      postcodeId: values.postcodeId,
      publicSite: values.publicSite,
      publicBigscreenSite: values.publicBigscreenSite,
      publicStudentViewType: values.publicStudentViewType,
      isClubPriorityEnabled: values.isClubPriorityEnabled,
      ageGroupsNaming: values.ageGroupsNaming,
      isEventUpdateNotificationCustomMessageEnabled: values.isEventUpdateNotificationCustomMessageEnabled
    };

    const { email, sportsDepartmentEmail } = data;

    if (email === '') {
      propz.set(data, ['email'], null);
    }

    if (sportsDepartmentEmail === '') {
      propz.set(data, ['sportsDepartmentEmail'], null);
    }

    const { code, phone, ...rest } = data;
    const phoneValue = typeof phone !== 'undefined' ? `${code}${phone}` : `${code}`;

    const dataWithPhone = {
      ...rest,
      phone: phoneValue
    };

    this.setState({
      isLoading: true
    });

    updateSchoolSettings(user, dataWithPhone).then(() => {
      this.updateSchool();
    });
  };

  render() {
    const { isPhoneCodeOptionsVisible, isLoadingPhoto, isLoading, imagePreviewUrl, logoCropError } = this.state;
    const logoUrl = imagePreviewUrl || this.props.user.activeSchool.pic || '/dist/images/empty_pic_uploader_box.png';
    const { user } = this.props;
    const isUnion = user.activeSchool.kind === KIND.SCHOOL_UNION;

    const { userId } = user;
    const postcode = propz.get(user, ['activeSchool', 'postcode']);
    const postcodeWithoutPoint = {
      id: postcode.id,
      point: postcode.point,
      postcode: postcode.postcode,
      postcodeNoSpaces: postcode.postcodeNoSpaces,
      region: postcode.region
    };
    const phone = propz.get(user, ['activeSchool', 'phone'], '');
    const phoneObj = getPhoneAndCode(phone);
    const phoneCode = propz.get(phoneObj, ['code'], PHONE_CODE.UK);
    const phoneBody = propz.get(phoneObj, ['phone'], '');

    const logo = propz.get(user, ['activeSchool', 'pic'], '');

    type PhotoPreset = 'PRIVATE' | 'ALL_PARTICIPANT_PARENTS';
    const rawPreset = propz.get(user, ['activeSchool', 'photoAccessPreset'], 'PRIVATE') as string;

    const safePhotoPreset: PhotoPreset =
      rawPreset === 'ALL_PARTICIPANT_PARENTS' ? 'ALL_PARTICIPANT_PARENTS' : 'PRIVATE';

    const initialSchool = {
      pic: logo,
      ownerId: userId,
      name: propz.get(user, ['activeSchool', 'name'], ''),
      description: propz.get(user, ['activeSchool', 'description'], ''),
      postcode: postcodeWithoutPoint,
      code: phoneCode,
      phone: phoneBody,
      postcodeId: propz.get(user, ['activeSchool', 'postcode', 'id'], ''),
      email: propz.get(user, ['activeSchool', 'email'], ''),
      sportsDepartmentEmail: propz.get(user, ['activeSchool', 'sportsDepartmentEmail'], ''),
      address: propz.get(user, ['activeSchool', 'address'], ''),
      domain: String(propz.get(user, ['activeSchool', 'domain'], '') || ''),
      publicSite: propz.get(user, ['activeSchool', 'publicSite'], DEFAULT_PUBLIC_SITE),
      publicBigscreenSite: propz.get(user, ['activeSchool', 'publicBigscreenSite'], DEFAULT_PUBLIC_BIGSCREEN_SITE),
      publicStudentViewType: propz.get(user, ['activeSchool', 'publicStudentViewType'], 'SHORTEN_LASTNAME_ONLY'),
      photoAccessPreset: safePhotoPreset,
      ageGroupsNaming: propz.get(user, ['activeSchool', 'ageGroupsNaming'], ''),
      isEventUpdateNotificationCustomMessageEnabled: propz.get(
        user,
        ['activeSchool', 'isEventUpdateNotificationCustomMessageEnabled'],
        false
      ),
      defaultFixtureStartTime: propz.get(user, ['activeSchool', 'defaultFixtureStartTime'], '10:00'),
      defaultClubStartTime: propz.get(user, ['activeSchool', 'defaultClubStartTime'], '10:00')
    };

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="schoolSettingsPage schoolSettingsEditPage">
        <header className="schoolSettingsHeader">
          <h1>{isUnion ? 'Union settings' : 'School settings'}</h1>
          <SchoolSettingsTabs isUnion={isUnion} />
        </header>

        <div className="schoolSettingsEditCard">
          <Formik
            initialValues={initialSchool}
            validationSchema={this.getSchoolSchema()}
            onSubmit={values => this.onSubmit(values)}
          >
            {({ touched, errors, setFieldValue, values, dirty, resetForm }) => {
              return (
                <Form className="schoolSettingsEditForm">
                  <section className="schoolSettingsEditSection schoolSettingsCard">
                    <div className="schoolSettingsEditSection__header">
                      <span className="schoolSettingsEditSection__icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <path d="M3 21h18M5 21V10l7-6 7 6v11M9 21v-6h6v6" />
                        </svg>
                      </span>
                      <div>
                        <h3>School identity</h3>
                        <p>How your school appears across Squad In Touch.</p>
                      </div>
                    </div>
                    <div className="schoolSettingsEditSection__body schoolSettingsEditSection__body--identity">
                      <Field name="pic">
                        {() => (
                          <div className="schoolSettingsLogoEditor">
                            <div className="bEditSchoolLogoFrame">
                              {isLoadingPhoto ? (
                                <div className="bEditSchoolLogoLoader">
                                  <Loader />
                                </div>
                              ) : (
                                <div className="bEditSchoolLogo" style={{ backgroundImage: `url(${logoUrl})` }}>
                                  <input
                                    type="file"
                                    name="pic"
                                    id="schoolLogo"
                                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                    onChange={this.onChangeLogoClick}
                                  />
                                  <label
                                    htmlFor="schoolLogo"
                                    title="Choose a new school logo"
                                    aria-label="Choose a new school logo"
                                  >
                                    +
                                  </label>
                                </div>
                              )}
                            </div>
                            <div className="schoolSettingsLogoEditor__copy">
                              <div>School logo</div>
                              <p>
                                JPG, PNG, WebP or SVG, at least 200 x 200px. You will crop it to a square before upload.
                              </p>
                            </div>
                            {logoCropError && !this.state.isLogoCropOpen && (
                              <div className="schoolSettingsLogoEditor__error">{logoCropError}</div>
                            )}
                          </div>
                        )}
                      </Field>

                      <div className="schoolSettingsEditField">
                        <label>Name</label>
                        <Field type="text" name="name" className="form-control" disabled />
                        <small>Contact support to change your school's registered name.</small>
                      </div>

                      <div className="schoolSettingsEditField schoolSettingsEditField--wide">
                        <label>Description</label>
                        <Field component="textarea" name="description" className="form-control" />
                      </div>
                    </div>
                  </section>

                  <section
                    className={`schoolSettingsEditSection schoolSettingsCard ${
                      isPhoneCodeOptionsVisible ? 'schoolSettingsEditSection--phoneMenuOpen' : ''
                    }`}
                  >
                    <div className="schoolSettingsEditSection__header">
                      <span className="schoolSettingsEditSection__icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="m3 7 9 6 9-6" />
                        </svg>
                      </span>
                      <div>
                        <h3>Contact details</h3>
                        <p>Where school and sport communications are sent.</p>
                      </div>
                    </div>
                    <div className="schoolSettingsEditSection__body">
                      <div className="schoolSettingsEditField">
                        <label>School official email</label>
                        <Field type="text" name="email" className="form-control" />
                        {touched.email && errors.email && (
                          <div className="schoolSettingsFieldError">{errors.email}</div>
                        )}
                      </div>

                      <div className="schoolSettingsEditField">
                        <label>Sports department email</label>
                        <Field type="text" name="sportsDepartmentEmail" className="form-control" />
                        {touched.sportsDepartmentEmail && errors.sportsDepartmentEmail && (
                          <div className="schoolSettingsFieldError">{errors.sportsDepartmentEmail}</div>
                        )}
                      </div>

                      <div className="schoolSettingsEditField">
                        <label>Phone</label>
                        <Field name="phone">
                          {() => (
                            <div className="schoolSettingsPhoneWrapper">
                              <div className="schoolSettingsPhone">
                                <button
                                  type="button"
                                  className="schoolSettingsPhone__codeButton"
                                  onClick={this.onPhoneCodeClick}
                                >
                                  {this.getPhoneCodeWithFlag(values.code)}
                                </button>
                                <Field type="text" name="phone" className="form-control schoolSettingsPhone__number" />
                              </div>
                              {isPhoneCodeOptionsVisible && (
                                <PhoneCodesList
                                  onClick={phoneCode => {
                                    setFieldValue('code', phoneCode);
                                    this.setState({ isPhoneCodeOptionsVisible: false });
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </Field>
                      </div>

                      <div className="schoolSettingsEditField">
                        <label>Postcode</label>
                        <Field name="postcode.postcode">
                          {() => (
                            <Autocomplete
                              searchFunction={text => {
                                const { user } = this.props;
                                const textWithoutSpaces = text.split(' ').join('');

                                setFieldValue('postcode.postcode', text);
                                setFieldValue('postcode.postcodeNoSpaces', textWithoutSpaces);

                                return searchFunctionSchoolPostcodes(user, text);
                              }}
                              getElementTitle={getPostcode}
                              customClass="mFullWidth schoolSettingsAutocomplete"
                              defaultItem={values.postcode}
                              placeholder="Start typing the postcode name"
                              onSelect={postcode => {
                                const result = {
                                  id: postcode.id,
                                  point: postcode.point,
                                  postcode: postcode.postcode,
                                  postcodeNoSpaces: postcode.postcodeNoSpaces,
                                  region: postcode.region
                                };

                                setFieldValue('postcode', result);
                                setFieldValue('postcodeId', postcode.id);
                              }}
                            />
                          )}
                        </Field>
                        {touched.postcode && errors.postcode && (
                          <div className="schoolSettingsFieldError">
                            {typeof errors.postcode === 'string' ? errors.postcode : (errors.postcode as any).postcode}
                          </div>
                        )}
                      </div>

                      <div className="schoolSettingsEditField schoolSettingsEditField--wide">
                        <label>Address</label>
                        <Field type="text" name="address" className="form-control" />
                      </div>
                    </div>
                  </section>

                  <section className="schoolSettingsEditSection schoolSettingsCard">
                    <div className="schoolSettingsEditSection__header">
                      <span className="schoolSettingsEditSection__icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
                        </svg>
                      </span>
                      <div>
                        <h3>Public website & bigscreen</h3>
                        <p>Public site, bigscreen and student name visibility.</p>
                      </div>
                    </div>
                    <div className="schoolSettingsEditSection__body">
                      <div className="schoolSettingsEditField schoolSettingsEditField--wide">
                        <label>Public domain (optional)</label>
                        <div className="schoolSettingsDomainField">
                          <Field type="text" name="domain" className="form-control" />
                          <span>.squadintouch.com</span>
                          {String(values.domain || '').trim() && (
                            <a
                              className="schoolSettingsDomainField__link"
                              href={getSchoolPublicSiteLink(String(values.domain || '').trim()).link}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open public school website"
                              aria-label="Open public school website"
                            >
                              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M14 5h5v5M19 5l-8 8M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
                              </svg>
                            </a>
                          )}
                        </div>
                        {touched.domain && errors.domain && (
                          <div className="schoolSettingsFieldError">{errors.domain}</div>
                        )}
                      </div>

                      <div className="schoolSettingsEditField">
                        <label>Public site access</label>
                        <Field name="publicSite.status">
                          {({ field }) => (
                            <SelectField
                              customClass="mMaxWidthNone"
                              items={getSelectOptionsForPublicSiteAccess()}
                              field={field}
                              onChange={event => setFieldValue('publicSite.status', event.target.value)}
                              value={values.publicSite.status}
                            />
                          )}
                        </Field>
                      </div>

                      {values.publicSite.status === 'PROTECTED' && (
                        <div className="schoolSettingsEditField">
                          <label>Public site password</label>
                          <Field type="text" name="publicSite.password" className="form-control" />
                        </div>
                      )}

                      <div className="schoolSettingsEditField">
                        <label>Show Twitter feed</label>
                        <Field name="publicSite.isTwitterVisible">
                          {({ field }) => (
                            <SelectField
                              customClass="mMaxWidthNone"
                              items={TWITTER_STATUS_OPTIONS}
                              field={field}
                              onChange={event =>
                                setFieldValue('publicSite.isTwitterVisible', event.target.value === 'true')
                              }
                              value={values.publicSite.isTwitterVisible}
                            />
                          )}
                        </Field>
                      </div>

                      <div className="schoolSettingsEditField">
                        <label>Public bigscreen access</label>
                        <Field name="publicBigscreenSite.status">
                          {({ field }) => (
                            <SelectField
                              customClass="mMaxWidthNone"
                              items={getSelectOptionsForPublicSiteAccess()}
                              field={field}
                              onChange={event => setFieldValue('publicBigscreenSite.status', event.target.value)}
                              value={values.publicBigscreenSite.status}
                            />
                          )}
                        </Field>
                      </div>

                      {values.publicBigscreenSite.status === SCHOOL_STATUS.PROTECTED && (
                        <div className="schoolSettingsEditField">
                          <label>Public bigscreen password</label>
                          <Field type="text" name="publicBigscreenSite.password" className="form-control" />
                        </div>
                      )}

                      <div className="schoolSettingsEditField">
                        <label>Public student view</label>
                        <Field name="publicStudentViewType">
                          {({ field }) => (
                            <SelectField
                              customClass="mMaxWidthNone"
                              items={getSelectOptionsForStudentNameView()}
                              field={field}
                              onChange={event => setFieldValue('publicStudentViewType', event.target.value)}
                              value={values.publicStudentViewType}
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                  </section>

                  <section className="schoolSettingsEditSection schoolSettingsCard">
                    <div className="schoolSettingsEditSection__header">
                      <span className="schoolSettingsEditSection__icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 7v5l3 2" />
                        </svg>
                      </span>
                      <div>
                        <h3>Defaults & scheduling</h3>
                        <p>Defaults applied to fixtures, clubs and media access.</p>
                      </div>
                    </div>
                    <div className="schoolSettingsEditSection__body">
                      <div className="schoolSettingsEditField">
                        <label>Default photo preset</label>
                        <Field name="photoAccessPreset">
                          {({ field }) => (
                            <SelectField
                              customClass="mMaxWidthNone"
                              items={PHOTO_ACCESS_PRESET_OPTIONS}
                              field={field}
                              onChange={event => setFieldValue('photoAccessPreset', event.target.value)}
                              value={values.photoAccessPreset}
                            />
                          )}
                        </Field>
                        <small>Used as the default for new albums/photos. You can still override per item.</small>
                      </div>

                      <div className="schoolSettingsEditField">
                        <label>Age groups naming</label>
                        <Field name="ageGroupsNaming">
                          {({ field }) => (
                            <SelectField
                              customClass="mMaxWidthNone"
                              items={getSelectOptionsForAgesGroupNaming()}
                              field={field}
                              onChange={event => setFieldValue('ageGroupsNaming', event.target.value)}
                              value={values.ageGroupsNaming}
                            />
                          )}
                        </Field>
                      </div>

                      <div className="schoolSettingsEditField schoolSettingsEditField--time">
                        <Field name="defaultClubStartTime">
                          {() => (
                            <TimePicker
                              label="Club default start time"
                              value={parseSchoolDefaultTime(values.defaultClubStartTime)}
                              onChange={(time: Date | null) =>
                                setFieldValue('defaultClubStartTime', formatSchoolDefaultTime(time))
                              }
                              minuteStep={5}
                              showConfirm
                              className="schoolSettingsEditTimePicker"
                            />
                          )}
                        </Field>
                      </div>

                      <div className="schoolSettingsEditField schoolSettingsEditField--time">
                        <Field name="defaultFixtureStartTime">
                          {() => (
                            <TimePicker
                              label="Fixture default start time"
                              value={parseSchoolDefaultTime(values.defaultFixtureStartTime)}
                              onChange={(time: Date | null) =>
                                setFieldValue('defaultFixtureStartTime', formatSchoolDefaultTime(time))
                              }
                              minuteStep={5}
                              showConfirm
                              className="schoolSettingsEditTimePicker"
                            />
                          )}
                        </Field>
                      </div>

                      <div className="schoolSettingsEditToggle schoolSettingsEditField--wide">
                        <div>
                          <div className="schoolSettingsEditToggle__title">Custom message on event updates</div>
                          <p>Let staff add a note when an event time or venue changes.</p>
                        </div>
                        <Field name="isEventUpdateNotificationCustomMessageEnabled">
                          {() => (
                            <Switch
                              value={values.isEventUpdateNotificationCustomMessageEnabled}
                              onChange={() => {
                                setFieldValue(
                                  'isEventUpdateNotificationCustomMessageEnabled',
                                  !values.isEventUpdateNotificationCustomMessageEnabled
                                );
                              }}
                              text={''}
                              name="isEventUpdateNotificationCustomMessageEnabled"
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                  </section>

                  {dirty && (
                    <div className="schoolSettingsEditActions" role="region" aria-label="Unsaved changes">
                      <span>You have unsaved changes</span>
                      <div>
                        <button
                          type="button"
                          className="schoolSettingsButton schoolSettingsButton--secondary"
                          onClick={() => {
                            resetForm();
                            this.setState({ imagePreviewUrl: '', isPhoneCodeOptionsVisible: false });
                          }}
                        >
                          Discard
                        </button>
                        <button type="submit" className="schoolSettingsButton schoolSettingsButton--primary">
                          Save changes
                        </button>
                      </div>
                    </div>
                  )}
                  {this.renderLogoCropModal(setFieldValue)}
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    );
  }
}
